import { isDefined } from '@omnea-app/packages/universal/type-guards/isDefined'
import { RiskMethodologyCalculator } from '../edit/shared/form/sections/risk-level/chips/content/data/RiskMethodologyCalculator'
import { RiskMethodologyCalculatorFactory } from '../edit/shared/form/sections/risk-level/chips/content/data/RiskMethodologyCalculatorFactory'
import { useGetRiskSettings } from '../settings/hooks/useGetRiskSettings'

type RiskLevelDetail = {
  id: string
  score: number
}

type RiskMatrixDetail = {
  score: number
}

type Input = Array<{
  inherentRiskProbability: RiskLevelDetail
  inherentRiskImpact: RiskLevelDetail
  inherentRiskMatrix: RiskMatrixDetail | null
  residualRiskProbability: RiskLevelDetail
  residualRiskImpact: RiskLevelDetail
  residualRiskMatrix: RiskMatrixDetail | null
}> | null

export type MaxRiskScore = {
  inherentRisk: {
    probability: RiskLevelDetail
    impact: RiskLevelDetail
    score: number
  }
  residualRisk: {
    probability: RiskLevelDetail
    impact: RiskLevelDetail
    score: number
  }
}

type Output = {
  getMaxRiskScoresInList: (risks?: Input) => MaxRiskScore | null
}

const getMaxScore =
  (riskMethodologyCalculator: RiskMethodologyCalculator) => (risks: NonNullable<Input>, type: 'inherent' | 'residual') =>
    risks.reduce(
      (acc, risk) => {
        const maxScoreForRisk = riskMethodologyCalculator.getRiskScoreValue({
          score: risk[`${type}RiskMatrix`]?.score,
          probabilityScore: risk[`${type}RiskProbability`]?.score,
          impactScore: risk[`${type}RiskImpact`]?.score,
        })

        if (isDefined(maxScoreForRisk) && maxScoreForRisk > acc.score) {
          return {
            probability: risk[`${type}RiskProbability`],
            impact: risk[`${type}RiskImpact`],
            score: maxScoreForRisk,
          }
        }
        return acc
      },
      { probability: { id: '', score: 0 }, impact: { id: '', score: 0 }, score: -1 }
    )

export const getMaxRiskScoresInListForMethodology =
  (riskMethodologyCalculator: RiskMethodologyCalculator) =>
  (risks?: Input): MaxRiskScore | null => {
    if (!risks || risks.length === 0) return null

    return {
      inherentRisk: getMaxScore(riskMethodologyCalculator)(risks, 'inherent'),
      residualRisk: getMaxScore(riskMethodologyCalculator)(risks, 'residual'),
    }
  }

export const useGetMaxRiskScoresInList = (): Output => {
  const riskSettings = useGetRiskSettings().data
  const riskMethodologyCalculator = RiskMethodologyCalculatorFactory(riskSettings?.riskScoreMethodology)

  return { getMaxRiskScoresInList: getMaxRiskScoresInListForMethodology(riskMethodologyCalculator) }
}
